"use client";

import { ReactNode, useContext, useEffect, useState } from "react";
import { useRouter } from "@bprogress/next";
import { ArchiveIcon, LoaderIcon, LogInIcon, PlayIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { WebinarActionWithSubscriptionList } from "@/components/webinar/webinar-action-with-subscription-list";
import { createPath } from "@/service/routing/routing";
import { webinarRegister } from "@/service/webinar-register";

const iconMap: Partial<Record<WebinarActionVM["subtype"], ReactNode>> = {
  registered: <PlayIcon />,
  can_register: <LogInIcon />,
  can_see_recording: <ArchiveIcon />,
};

/**
 * Przycisk zapisu na webinar wraz z modalem listy zainteresowanych.
 * Używa go panel akcji (`WebinarActionBlock`) i baner ekspertów, żeby oba
 * prowadziły tę samą rejestrację zamiast dublować logikę.
 *
 * Gdy użytkownik nie jest zalogowany, przycisk prowadzi do `fallbackHref`
 * (kotwica do bloku logowania) — sama rejestracja wymaga konta.
 */
export const WebinarRegisterButton = ({
  action,
  className,
  label,
  fallbackHref,
  showIcon = true,
}: {
  action: WebinarActionVM;
  className?: string;
  label?: ReactNode;
  fallbackHref?: string;
  showIcon?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isLoggedIn } = useContext(UserContext);
  const router = useRouter();

  useEffect(() => {
    setLoading(false);
  }, [action]);

  const { subscriptionListWidget, recordingAvailable, url, subtype } = action;
  const { alreadySubscribed, slug } = subscriptionListWidget ?? {};
  const withoutSubscriptionList = !subscriptionListWidget;

  const content = label ?? action.buttonName;

  if (!isLoggedIn) {
    return (
      <Button asChild variant={"blue"} className={className}>
        <a href={fallbackHref ?? createPath("security.login")}>{content}</a>
      </Button>
    );
  }

  const heading = recordingAvailable
    ? "Nagranie już dostępne"
    : "Nagranie dostępne już niedługo";
  const paragraph = recordingAvailable
    ? `Zapisz się na listę zainteresowywanych, żeby otrzymać dostęp do nagrania i przyszłych informacji o aktualizacjach.`
    : "Zapisz się na listę zainteresowanych, żeby jako pierwszy/a dostać informację o dostępnym nagraniu.";

  return (
    <>
      <Button
        variant={"blue"}
        className={className}
        onClick={async () => {
          if (withoutSubscriptionList && url) {
            window.location.href = url;
          }
          if (subtype === "can_see_recording") {
            window.location.href = url as string;
            return;
          }
          if (!withoutSubscriptionList) {
            setIsOpen(true);
          }

          if (subtype === "can_register") {
            setLoading(true);
            try {
              await webinarRegister(action.path as string);
              router.refresh();
            } finally {
              setLoading(false);
            }
          }
        }}
        disabled={loading}
      >
        {loading && <LoaderIcon className="animate-spin" />}
        {!loading && showIcon && subtype && iconMap[subtype]}
        {content}
      </Button>

      {!withoutSubscriptionList && (
        <WebinarActionWithSubscriptionList
          state={{
            apiSubscribe: createPath(
              "subscription_list.default.subscribe_api",
              { slug },
            ),
            apiUnsubscribe: createPath(
              "subscription_list.default.unsubscribe_api",
              { slug },
            ),
            saveToNewsletter: true,
            subscribed: alreadySubscribed as boolean,
            heading,
            paragraph,
            options: url ? { url } : {},
          }}
          close={() => setIsOpen(false)}
          isOpen={isOpen}
        />
      )}
    </>
  );
};
